import React, { useMemo, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, Radio, Select, Typography } from 'antd';
import { ml } from 'src/_shared/services/utils.js';
import { ALL_PROVIDERS, providerByValue } from './integrationCatalog.js';

const { Text } = Typography;

const NOTIFICATION_OPTIONS = [
	{ value: 'ALL', label: 'Notify All Employees' },
	{ value: 'DEPARTMENT', label: 'Notify Department Only' },
	{ value: 'NONE', label: 'No Notifications' },
];

/**
 * Add / update an ATS integration. Provider credential fields come from the
 * declarative catalog, replacing the 15 near-identical render methods (and the
 * hand-rolled validation) in the old settings form.
 */
const AddIntegrationDrawer = ({
	visible,
	onClose,
	onSubmit,
	currentUser,
	allMultiLingualData,
	managers,
	tieredBonuses,
	existingIntegrations,
	submitting,
}) => {
	const [form] = Form.useForm();
	const [providerValue, setProviderValue] = useState(null);
	const [dedupeError, setDedupeError] = useState(false);

	const provider = useMemo(() => providerByValue(providerValue), [providerValue]);
	const alreadyConnected = useMemo(
		() => (existingIntegrations || []).some((ats) => ats.ATSName === providerValue),
		[existingIntegrations, providerValue]
	);

	const adminOptions = useMemo(
		() =>
			(managers || [])
				.filter((manager) => manager.role === 'admin')
				.sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''))
				.map((manager) => ({
					value: manager.id,
					label: [manager.firstName, manager.lastName].filter(Boolean).join(' '),
				})),
		[managers]
	);

	const bonusOptions = useMemo(
		() => [
			{ value: 'none', label: 'None' },
			...(tieredBonuses || []).map((bonus) => ({ value: bonus.id, label: bonus.name })),
		],
		[tieredBonuses]
	);

	const handleClose = () => {
		form.resetFields();
		setProviderValue(null);
		setDedupeError(false);
		onClose();
	};

	const handleSubmit = async () => {
		setDedupeError(false);
		const values = await form.validateFields();
		const fieldValues = {};
		for (const field of provider?.fields || []) {
			if (values[field.name] !== undefined && values[field.name] !== '') {
				fieldValues[field.name] = values[field.name];
			}
		}

		// Multi-instance providers (iCIMS, TalentReef) must be unique on their
		// identifying fields.
		if (provider?.dedupeKeys) {
			const duplicate = (existingIntegrations || []).some(
				(ats) =>
					ats.ATSName === provider.value &&
					provider.dedupeKeys.every((key) => ats[key] === fieldValues[key])
			);
			if (duplicate) {
				setDedupeError(true);
				return;
			}
		}

		const manager = adminOptions.find((option) => option.value === values.hiringManagerId);
		const bonus =
			values.bonusBuilderId && values.bonusBuilderId !== 'none'
				? (tieredBonuses || []).find((b) => b.id === values.bonusBuilderId)
				: null;

		const ok = await onSubmit({
			provider,
			fieldValues,
			hiringManager: manager ? { id: manager.value, name: manager.label } : null,
			bonusBuilder: bonus ? { tieredBonusId: bonus.id, name: bonus.name } : null,
			notificationType: values.notificationType || 'NONE',
		});
		if (ok) handleClose();
	};

	return (
		<Drawer
			destroyOnClose
			title={ml('Connect an ATS / HRIS', currentUser, allMultiLingualData)}
			width={480}
			visible={visible}
			onClose={handleClose}
			footer={
				<div style={{ textAlign: 'right' }}>
					<Button style={{ marginRight: 8 }} onClick={handleClose}>
						{ml('Cancel', currentUser, allMultiLingualData)}
					</Button>
					<Button
						type="primary"
						loading={submitting}
						disabled={!providerValue}
						onClick={handleSubmit}
					>
						{alreadyConnected
							? ml('Update Integration', currentUser, allMultiLingualData)
							: ml('Connect', currentUser, allMultiLingualData)}
					</Button>
				</div>
			}
		>
			<Form form={form} layout="vertical" initialValues={{ notificationType: 'NONE' }}>
				<Form.Item
					label={ml('Provider', currentUser, allMultiLingualData)}
					required
					style={{ marginBottom: alreadyConnected ? 4 : undefined }}
				>
					<Select
						showSearch
						placeholder="Search providers…"
						optionFilterProp="label"
						value={providerValue}
						options={[
							{
								label: 'Managed integrations',
								options: ALL_PROVIDERS.filter((p) => p.family === 'workato').map((p) => ({
									value: p.value,
									label: p.title,
								})),
							},
							{
								label: 'Direct integrations',
								options: ALL_PROVIDERS.filter((p) => p.family === 'legacy').map((p) => ({
									value: p.value,
									label: p.title,
								})),
							},
						]}
						onChange={(value) => setProviderValue(value)}
					/>
				</Form.Item>
				{alreadyConnected && !provider?.dedupeKeys ? (
					<Alert
						showIcon
						type="info"
						style={{ marginBottom: 16 }}
						message="Already connected — saving will update the existing configuration."
					/>
				) : null}

				{(provider?.fields || []).map((field) => (
					<Form.Item
						key={field.name}
						name={field.name}
						label={ml(field.label, currentUser, allMultiLingualData)}
						rules={[{ required: true, message: `${field.label} is required.` }]}
					>
						{field.secret ? (
							<Input.Password placeholder={`Enter ${field.label}`} autoComplete="new-password" />
						) : (
							<Input placeholder={`Enter ${field.label}`} />
						)}
					</Form.Item>
				))}

				<Form.Item
					name="hiringManagerId"
					label={ml('Hiring Contact', currentUser, allMultiLingualData)}
					rules={[{ required: true, message: 'Choose a hiring contact.' }]}
				>
					<Select
						showSearch
						placeholder="Ex. James Smith"
						optionFilterProp="label"
						options={adminOptions}
					/>
				</Form.Item>

				<Form.Item
					name="bonusBuilderId"
					label={ml('Referral Bonus', currentUser, allMultiLingualData)}
				>
					<Select
						showSearch
						placeholder="Choose a Bonus Build"
						optionFilterProp="label"
						options={bonusOptions}
					/>
				</Form.Item>

				<Form.Item
					name="notificationType"
					label={ml('Notification', currentUser, allMultiLingualData)}
				>
					<Radio.Group>
						{NOTIFICATION_OPTIONS.map((option) => (
							<Radio.Button key={option.value} value={option.value}>
								{ml(option.label, currentUser, allMultiLingualData)}
							</Radio.Button>
						))}
					</Radio.Group>
				</Form.Item>

				{dedupeError ? (
					<Alert
						showIcon
						closable
						type="error"
						message="An integration with these identifiers already exists."
						onClose={() => setDedupeError(false)}
					/>
				) : null}
				{provider?.family === 'workato' ? (
					<Text type="secondary" style={{ fontSize: 12 }}>
						Managed integrations are provisioned by the ERIN team — connecting one
						here registers it for your company; credentials are exchanged securely
						during onboarding.
					</Text>
				) : null}
			</Form>
		</Drawer>
	);
};

export default AddIntegrationDrawer;
